/**
 * 自适应音效管理器
 * 优先使用 Web Audio API，不支持时自动降级到 HTML5 Audio
 */
import { AudioManager } from './audio-manager';
import type { PlayOptions, AudioConfigMap } from './types';
import { SoundEffectManagerWebAudio } from './sound-effect-manager-web-audio';
import { SoundEffectManager } from './sound-effect-manager';
import { isAudioContextSupported } from './audio-utils';

/**
 * 自适应音效管理器
 * 自动选择最佳实现（Web Audio API 或 HTML5 Audio）
 */
export class SoundEffectManagerAdaptive {
  private implementation: SoundEffectManagerWebAudio | SoundEffectManager;
  private isWebAudio: boolean;

  constructor(audioManager: AudioManager) {
    this.isWebAudio = isAudioContextSupported();

    if (this.isWebAudio) {
      console.log('[SoundEffectManager] Using Web Audio API implementation');
      this.implementation = new SoundEffectManagerWebAudio(audioManager);
    } else {
      console.warn('[SoundEffectManager] AudioContext not supported, falling back to HTML5 Audio');
      this.implementation = new SoundEffectManager(audioManager);
    }
  }

  /**
   * 获取当前使用的实现类型
   */
  getImplementationType(): 'web-audio' | 'html5-audio' {
    return this.isWebAudio ? 'web-audio' : 'html5-audio';
  }

  /**
   * 是否使用 Web Audio API
   */
  isUsingWebAudio(): boolean {
    return this.isWebAudio;
  }

  // ==================== 以下方法代理到具体实现 ====================

  register(id: string, path: string): void {
    this.implementation.register(id, path);
  }

  registerAll(configMap: AudioConfigMap): void {
    this.implementation.registerAll(configMap);
  }

  isRegistered(id: string): boolean {
    return this.implementation.isRegistered(id);
  }

  unregister(id: string): void {
    this.implementation.unregister(id);
  }

  async preload(id: string): Promise<void> {
    return this.implementation.preload(id);
  }

  async preloadAll(ids?: string[]): Promise<Array<{ id: string; success: boolean; error?: Error; }>> {
    return this.implementation.preloadAll(ids);
  }

  async playSound(id: string, options?: PlayOptions): Promise<void> {
    return this.implementation.playSound(id, options);
  }

  async playLoopingSound(id: string, uniqueKey: string, options?: PlayOptions): Promise<void> {
    return this.implementation.playLoopingSound(id, uniqueKey, options);
  }

  async switchLoopingSound(newId: string, uniqueKey: string, options?: PlayOptions): Promise<void> {
    return this.implementation.switchLoopingSound(newId, uniqueKey, options);
  }

  stopLoopingSound(uniqueKey: string): void {
    this.implementation.stopLoopingSound(uniqueKey);
  }

  stopAllSounds(): void {
    this.implementation.stopAllSounds();
  }

  updateVolume(): void {
    this.implementation.updateVolume();
  }

  /**
   * 解锁音频上下文（仅 Web Audio 实现支持）
   * HTML5 Audio 实现会返回 true（不需要解锁）
   */
  async unlockAudioContext(): Promise<boolean> {
    if (this.isWebAudio && 'unlockAudioContext' in this.implementation) {
      return (this.implementation as SoundEffectManagerWebAudio).unlockAudioContext();
    }
    // HTML5 Audio 不需要解锁，直接返回成功
    return true;
  }

  /**
   * 销毁管理器（仅 Web Audio 实现支持）
   */
  destroy(): void {
    if ('destroy' in this.implementation) {
      (this.implementation as SoundEffectManagerWebAudio).destroy();
    }
  }
}
